import { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslations } from "next-intl";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  Skeleton,
  useToast,
} from "@chotca/ui";
import { MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import {
  PROVINCE_NAME_VI,
  type OrgAddress,
  type ProvinceCode,
} from "@chotca/config";
import {
  useAdminArchiveOrgAddress,
  useAdminCreateOrgAddress,
  useAdminOrgAddresses,
  useAdminUpdateOrgAddress,
} from "@chotca/api-client";
import { AddressPicker, type AddressValue } from "@/components/address-picker";

/**
 * Admin-side branches (`OrgAddress`) management — mirrors the employer
 * `BranchesCard` but talks to the admin cross-org endpoints. Lets concierge
 * staff add / edit / archive a branch on an org's behalf.
 */
export function AdminOrgBranchesCard({ orgId }: { orgId: string }) {
  const t = useTranslations("admin");
  const addressesQ = useAdminOrgAddresses(orgId);
  const addresses = useMemo(
    () => (addressesQ.data?.items ?? []).filter((a) => !a.archivedAt),
    [addressesQ.data],
  );

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<OrgAddress | null>(null);
  const [archiving, setArchiving] = useState<OrgAddress | null>(null);

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }
  function openEdit(addr: OrgAddress) {
    setEditing(addr);
    setFormOpen(true);
  }

  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-title-lg font-bold text-ink">
            {t("branchesTitle")}
          </h2>
          <p className="mt-1 text-sm text-ash">{t("branchesDesc")}</p>
        </div>
        <Button onClick={openCreate} size="sm">
          <Plus size={16} />
          {t("branchAddCta")}
        </Button>
      </div>

      <div className="mt-5 flex flex-col gap-2">
        {addressesQ.isLoading ? (
          <>
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </>
        ) : addresses.length === 0 ? (
          <p className="rounded-lg border border-dashed border-smoke p-6 text-center text-sm text-ash">
            {t("branchEmpty")}
          </p>
        ) : (
          addresses.map((a) => (
            <BranchRow
              key={a.id}
              address={a}
              canArchive={addresses.length > 1}
              onEdit={() => openEdit(a)}
              onArchive={() => setArchiving(a)}
            />
          ))
        )}
      </div>

      {formOpen ? (
        <BranchFormDialog
          orgId={orgId}
          open={formOpen}
          onClose={() => setFormOpen(false)}
          editing={editing}
        />
      ) : null}

      <ArchiveBranchDialog
        orgId={orgId}
        target={archiving}
        onClose={() => setArchiving(null)}
      />
    </Card>
  );
}

function BranchRow({
  address,
  canArchive,
  onEdit,
  onArchive,
}: {
  address: OrgAddress;
  canArchive: boolean;
  onEdit: () => void;
  onArchive: () => void;
}) {
  const t = useTranslations("admin");
  const inUse = address.inUseByOpenPostings > 0;
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-smoke p-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-mist text-ink">
          <MapPin size={16} />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-sm font-bold text-ink">
              {address.alias}
            </p>
            {inUse ? (
              <Badge tone="lime-soft">
                {t("branchInUse")} · {address.inUseByOpenPostings} tin
              </Badge>
            ) : null}
          </div>
          <p className="mt-0.5 text-sm text-ash">{address.address}</p>
          <p className="text-xs text-ash">
            {address.district}, {address.city}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button size="iconSm" variant="ghost" onClick={onEdit} aria-label="Sửa">
          <Pencil size={14} />
        </Button>
        <Button
          size="iconSm"
          variant="ghost"
          onClick={onArchive}
          disabled={!canArchive || inUse}
          aria-label="Xoá"
          title={
            inUse
              ? t("branchInUseHint")
              : !canArchive
                ? t("branchKeepOneHint")
                : t("branchAddCta")
          }
        >
          <Trash2 size={14} />
        </Button>
      </div>
    </div>
  );
}

// Address is "ready" once a Google Maps paste has filled coords + province.
function addressReady(a: AddressValue): boolean {
  return (
    a.address.trim().length > 0 &&
    // Ward is mandatory (province + phường/xã) — district is legacy.
    !!a.wardProvinceCode &&
    !!a.wardCode &&
    a.lat !== null &&
    a.lng !== null
  );
}

const branchFormSchema = z.object({
  alias: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập tên gợi nhớ")
    .max(60, "Tên gợi nhớ tối đa 60 ký tự"),
  address: z
    .custom<AddressValue>()
    .refine(addressReady, "Hãy dán link Google Maps để lấy địa chỉ."),
});

type BranchFormValues = z.infer<typeof branchFormSchema>;

function BranchFormDialog({
  orgId,
  open,
  onClose,
  editing,
}: {
  orgId: string;
  open: boolean;
  onClose: () => void;
  editing: OrgAddress | null;
}) {
  const t = useTranslations("admin");
  const { success: toastSuccess, error: toastError } = useToast();
  const create = useAdminCreateOrgAddress(orgId);
  const update = useAdminUpdateOrgAddress(orgId);

  const defaults = useMemo<BranchFormValues>(
    () => ({
      alias: editing?.alias ?? "",
      address: {
        address: editing?.address ?? "",
        wardCode: editing?.wardCode ?? "",
        wardName: editing?.wardName ?? "",
        wardProvinceCode: editing?.wardProvinceCode ?? "",
        district: editing?.district ?? "",
        city: editing?.city ?? "",
        provinceCode: editing?.provinceCode ?? null,
        provinceName: editing?.provinceCode
          ? (PROVINCE_NAME_VI[editing.provinceCode as ProvinceCode] ?? null)
          : null,
        lat: editing?.lat ?? null,
        lng: editing?.lng ?? null,
        mapUrl: editing?.mapUrl ?? null,
      },
    }),
    [editing],
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BranchFormValues>({
    resolver: zodResolver(branchFormSchema),
    mode: "onTouched",
    defaultValues: defaults,
  });

  useEffect(() => {
    if (open) reset(defaults);
  }, [open, defaults, reset]);

  const pending = create.isPending || update.isPending;

  const onSave = handleSubmit(async (values) => {
    const a = values.address;
    const body = {
      alias: values.alias,
      address: a.address,
      district: a.district,
      city: a.city,
      provinceCode: a.provinceCode ?? undefined,
      wardCode: a.wardCode,
      wardName: a.wardName,
      wardProvinceCode: a.wardProvinceCode,
      lat: a.lat as number,
      lng: a.lng as number,
      mapUrl: a.mapUrl,
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, body });
      } else {
        await create.mutateAsync(body);
      }
      toastSuccess(t("branchSaved"));
      onClose();
    } catch (e) {
      toastError(e instanceof Error ? e.message : "Lưu chi nhánh thất bại");
    }
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? t("branchEditTitle") : t("branchAddTitle")}
      description={t("branchFormDesc")}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Hủy
          </Button>
          <Button onClick={onSave} disabled={pending} loading={pending}>
            {editing ? "Lưu thay đổi" : t("branchAddCta")}
          </Button>
        </>
      }
    >
      <form className="flex flex-col gap-4" onSubmit={onSave} noValidate>
        <FormField label={t("branchAlias")} error={errors.alias?.message}>
          <Input
            maxLength={60}
            placeholder="Vd. Chi nhánh Q1"
            autoFocus
            invalid={!!errors.alias}
            {...register("alias")}
          />
        </FormField>

        <Controller
          control={control}
          name="address"
          render={({ field }) => (
            <AddressPicker
              label="Địa chỉ chi nhánh"
              description="Dán link Google Maps để lấy toạ độ chính xác — giúp nhân viên tính khoảng cách đúng."
              value={field.value}
              onChange={field.onChange}
              required
              error={errors.address?.message as string | undefined}
            />
          )}
        />
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}

function ArchiveBranchDialog({
  orgId,
  target,
  onClose,
}: {
  orgId: string;
  target: OrgAddress | null;
  onClose: () => void;
}) {
  const t = useTranslations("admin");
  const archive = useAdminArchiveOrgAddress(orgId);
  const { success: toastSuccess, error: toastError } = useToast();

  async function onConfirm() {
    if (!target) return;
    try {
      await archive.mutateAsync(target.id);
      toastSuccess(t("branchArchived"));
      onClose();
    } catch (e) {
      toastError(e instanceof Error ? e.message : "Không xoá được chi nhánh");
    }
  }

  return (
    <ConfirmDialog
      open={!!target}
      onClose={onClose}
      onConfirm={onConfirm}
      destructive
      title={t("branchArchiveTitle")}
      description={
        target ? (
          <>
            Bạn có chắc muốn xoá <strong>{target.alias}</strong>?{" "}
            {t("branchArchiveDesc")}
          </>
        ) : (
          ""
        )
      }
      confirmLabel="Xoá"
      loading={archive.isPending}
    />
  );
}
